/**
 * GameEngine — the state machine, and the only write path in the app.
 *
 * Pure TypeScript: no Three.js, no gsap, no DOM. It runs under vitest in node,
 * which is what makes the rules portable to a server later. Everything
 * downstream (render, motion, vfx, ui, audio) subscribes to the bus and reacts;
 * nothing downstream mutates state.
 *
 * Conventions worth knowing before you wire this up:
 *
 * - `snapshot.stock[0]` is the face-up `activeCard`; the rest follow in draw
 *   order. Playing pops from the front.
 * - Every snapshot handed out is a deep copy, so subscribers can keep one.
 * - `playOn` emits `card:play` with an *intermediate* snapshot (card on the
 *   pile, nothing resolved yet) and then `pile:resolve` with the settled state.
 *   That gives the choreographer a landing beat and a resolve beat.
 * - `pile:resolve` fires for every legal play, including plain landings — those
 *   carry `cleared: false` and are what resets a streak.
 * - The engine owns time. `tick(dt)` accumulates real seconds; a `tick` event is
 *   emitted only when the displayed whole second changes, so the bus stays calm
 *   at 60fps. Read `snapshot().secondsRemaining` for the value at any moment.
 */

import type { EventBus } from '@/core/bus';
import {
  MAX_BUSTS,
  PILE_COUNT,
  ROUND_SECONDS,
  type CardId,
  type FinalScore,
  type GameEvent,
  type GameOverReason,
  type GamePhase,
  type GameSnapshot,
  type PileTotals,
  type Resolution,
} from '@/game/types';
import { createShuffledDeck, randomSeed } from '@/game/deck';
import { resolvePile, totalsOf } from '@/game/rules';
import { levelFor, levelThreshold } from '@/game/levels';
import {
  applyOutcome,
  finalScore,
  initialScoreState,
  streakLevelOf,
  type ScoreState,
} from '@/game/scoring';

export interface GameEngineOptions {
  /** Shuffle seed. Omit for a fresh random game; pass one to replay exactly. */
  seed?: number;
  /** Round length in seconds. Defaults to 180. */
  seconds?: number;
  /**
   * Intro window before the clock starts, in seconds. While it runs the phase
   * is `dealing` and plays are rejected, which gives the renderer room for the
   * opening flourish. Defaults to 0 — straight into play.
   */
  dealSeconds?: number;
  /**
   * A stacked deck, used verbatim (never shuffled), index 0 dealt first.
   * For tests and scripted demos; leave it out for real games.
   */
  deck?: readonly CardId[];
}

interface UndoRecord {
  stock: CardId[];
  piles: CardId[][];
  /**
   * The whole score state, rather than a field per counter.
   *
   * It used to name its three fields individually, which meant every counter
   * added to `ScoreState` had to be remembered here too — and the failure mode
   * of forgetting is silent: undo quietly keeps a tally it should have rewound.
   */
  score: ScoreState;
}

/** Guards against floating-point dust when converting the clock to whole seconds. */
const CLOCK_EPSILON = 1e-6;

export class GameEngine {
  /** The seed this game was shuffled with — show it, log it, replay it. */
  readonly seed: number;
  /** Round length in seconds, as configured. */
  readonly seconds: number;

  readonly #bus: EventBus;
  readonly #dealSeconds: number;
  readonly #fixedDeck: readonly CardId[] | null;

  #phase: GamePhase = 'idle';
  #stock: CardId[] = [];
  #piles: CardId[][] = [];
  #score: ScoreState = initialScoreState();
  #remaining = 0;
  #dealRemaining = 0;
  #lastEmittedSecond = 0;
  #undo: UndoRecord | null = null;
  #final: FinalScore | null = null;

  constructor(bus: EventBus, options: GameEngineOptions = {}) {
    this.#bus = bus;
    this.seed = options.seed ?? randomSeed();
    this.seconds = Math.max(0, options.seconds ?? ROUND_SECONDS);
    this.#dealSeconds = Math.max(0, options.dealSeconds ?? 0);
    this.#fixedDeck = options.deck ? [...options.deck] : null;
    this.#piles = emptyPiles();
    this.#remaining = this.seconds;
    this.#lastEmittedSecond = this.seconds;
  }

  get phase(): GamePhase {
    return this.#phase;
  }

  get isOver(): boolean {
    return this.#phase === 'over';
  }

  /** The breakdown, available once the game is over. */
  get final(): FinalScore | null {
    return this.#final;
  }

  /** Deal a fresh round. Calling it again restarts from the same seed. */
  start(): GameSnapshot {
    this.#stock = this.#fixedDeck ? [...this.#fixedDeck] : createShuffledDeck(this.seed);
    this.#piles = emptyPiles();
    this.#score = initialScoreState();
    this.#remaining = this.seconds;
    this.#dealRemaining = this.#dealSeconds;
    this.#lastEmittedSecond = this.#displaySeconds();
    this.#undo = null;
    this.#final = null;
    this.#phase = this.#dealSeconds > 0 ? 'dealing' : 'playing';

    const snapshot = this.snapshot();
    this.#emit({ type: 'game:start', snapshot });
    return snapshot;
  }

  /**
   * The single player action: drop the face-up card on pile `pileIndex`.
   * Returns the resolution, or null when the play is not legal right now
   * (game not running, no card, bad index).
   */
  playOn(pileIndex: number): Resolution | null {
    if (this.#phase !== 'playing') return null;
    if (!Number.isInteger(pileIndex) || pileIndex < 0 || pileIndex >= PILE_COUNT) return null;

    const card = this.#stock[0];
    if (card === undefined) return null;

    this.#undo = {
      stock: [...this.#stock],
      piles: this.#piles.map((pile) => [...pile]),
      score: { ...this.#score },
    };

    // Bounds-checked against PILE_COUNT at the top of this method.
    const outcome = resolvePile(this.#piles[pileIndex]!, card);

    // Intermediate state: the card has left the stock and is sitting on the
    // pile, nothing has resolved yet. This is the beat the renderer animates,
    // so it is captured before the pile clears.
    const levelBefore = levelFor(this.#score.score);
    this.#stock.shift();
    this.#piles[pileIndex] = outcome.cards.slice();
    this.#phase = 'resolving';
    const landing = this.snapshot();

    if (outcome.cleared) this.#piles[pileIndex] = [];
    const scored = applyOutcome(this.#score, outcome);
    this.#score = scored.state;

    const resolution: Resolution = {
      pile: pileIndex,
      card,
      cards: outcome.cards.slice(),
      totals: { ...outcome.totals },
      cleared: outcome.cleared,
      is21: outcome.is21,
      isFiveCard: outcome.isFiveCard,
      isWild: outcome.isWild,
      isBust: outcome.isBust,
      bonuses: [...outcome.bonuses],
      basePoints: scored.basePoints,
      streakLevel: scored.streakLevel,
      streakPoints: scored.streakPoints,
      streakLabel: scored.streakLabel,
      pointsGained: scored.pointsGained,
      score: this.#score.score,
      busts: this.#score.busts,
    };

    try {
      this.#emit({ type: 'card:play', pile: pileIndex, card, snapshot: landing });
      this.#emit({ type: 'pile:resolve', resolution, snapshot: this.snapshot() });

      if (scored.pointsGained !== 0) {
        this.#emit({
          type: 'score:change',
          score: this.#score.score,
          delta: scored.pointsGained,
          snapshot: this.snapshot(),
        });
      }

      // Strictly after `score:change`: the HUD's level pill reads the new score
      // off that event, and a level-up that arrived first would celebrate a
      // number the player had not been shown yet.
      const levelAfter = levelFor(this.#score.score);
      if (levelAfter > levelBefore) {
        this.#emit({
          type: 'level:up',
          level: levelAfter,
          from: levelBefore,
          atScore: this.#score.score,
          nextThreshold: levelThreshold(levelAfter + 1),
          snapshot: this.snapshot(),
        });
      }

      if (outcome.isBust) {
        this.#emit({
          type: 'bust',
          pile: pileIndex,
          busts: this.#score.busts,
          snapshot: this.snapshot(),
        });
      }
    } finally {
      // A subscriber that throws must not strand the engine mid-resolution.
      this.#phase = 'playing';
    }

    if (this.#score.busts >= MAX_BUSTS) {
      this.#end('bustedOut');
    } else if (this.#stock.length === 0) {
      this.#end('deckEmpty');
    }

    return resolution;
  }

  /**
   * Take back the last play. One level only: undoing twice in a row fails.
   * Restores piles, stock, score, busts and streak exactly — but not the clock,
   * which keeps running the way it does in the original.
   */
  undo(): boolean {
    if (this.#phase !== 'playing' || this.#undo === null) return false;

    const record = this.#undo;
    this.#stock = [...record.stock];
    this.#piles = record.piles.map((pile) => [...pile]);
    this.#score = { ...record.score };
    this.#undo = null;

    this.#emit({ type: 'undo', snapshot: this.snapshot() });
    return true;
  }

  /**
   * Advance the clock by `deltaSeconds` of real time. Drives the intro window,
   * the round clock, and the timeout ending. Safe to call every frame.
   */
  tick(deltaSeconds: number): void {
    if (this.#phase !== 'playing' && this.#phase !== 'dealing') return;
    if (!Number.isFinite(deltaSeconds) || deltaSeconds <= 0) return;

    let dt = deltaSeconds;

    if (this.#phase === 'dealing') {
      const consumed = Math.min(this.#dealRemaining, dt);
      this.#dealRemaining -= consumed;
      dt -= consumed;
      if (this.#dealRemaining > 0) return;
      this.#phase = 'playing';
      if (dt <= 0) return;
    }

    this.#remaining = Math.max(0, this.#remaining - dt);

    const second = this.#displaySeconds();
    if (second !== this.#lastEmittedSecond) {
      this.#lastEmittedSecond = second;
      this.#emit({ type: 'tick', secondsRemaining: second, snapshot: this.snapshot() });
    }

    if (this.#remaining <= 0) this.#end('timeout');
  }

  /** A deep copy of everything a renderer needs. Safe to keep. */
  snapshot(): GameSnapshot {
    return {
      phase: this.#phase,
      activeCard: this.#stock.length > 0 ? this.#stock[0]! : null,
      stock: [...this.#stock],
      piles: this.#piles.map((pile) => [...pile]),
      pileTotals: this.#piles.map((pile): PileTotals => totalsOf(pile)),
      score: this.#score.score,
      busts: this.#score.busts,
      secondsRemaining: this.#displaySeconds(),
      streakLevel: streakLevelOf(this.#score.streak),
      level: levelFor(this.#score.score),
      // `resolving` only exists inside the synchronous body of playOn, so the
      // undo affordance stays lit for the snapshots emitted during a resolution.
      canUndo:
        this.#undo !== null && (this.#phase === 'playing' || this.#phase === 'resolving'),
    };
  }

  /** Cards that will never be played: stock plus everything still in the piles. */
  #cardsRemaining(): number {
    return this.#stock.length + this.#piles.reduce((sum, pile) => sum + pile.length, 0);
  }

  #displaySeconds(): number {
    return Math.max(0, Math.ceil(this.#remaining - CLOCK_EPSILON));
  }

  #end(reason: GameOverReason): void {
    if (this.#phase === 'over') return;

    const cardsRemaining = this.#cardsRemaining();
    const perfectGame = cardsRemaining === 0;

    const final = finalScore({
      score: this.#score.score,
      busts: this.#score.busts,
      clears: this.#score.clears,
      bestStreak: this.#score.bestStreak,
      plays: this.#score.plays,
      secondsRemaining: this.#displaySeconds(),
      cardsRemaining,
      perfectGame,
      reason,
    });

    this.#final = final;
    this.#score = { ...this.#score, score: final.total };
    this.#undo = null;
    this.#phase = 'over';

    this.#emit({ type: 'game:over', final, snapshot: this.snapshot() });
  }

  #emit(event: GameEvent): void {
    this.#bus.emit(event);
  }
}

function emptyPiles(): CardId[][] {
  return Array.from({ length: PILE_COUNT }, (): CardId[] => []);
}
